(function () {
  'use strict';

  // Shopify replaces section DOM without reloading layout scripts. Rebind the
  // current elements, rather than leaving the replacement form without JS.
  if (window.detaloraCommerce?.reinitialize) {
    window.detaloraCommerce.reinitialize();
    return;
  }
  let root;
  let drawer;
  let overlay;
  let listenerCleanups = [];
  let observers = [];
  let reinitializeTimer;
  const routes = window.cabinFixRoutes || {};
  const state = {
    cart: null,
    cartBusy: false,
    activeModel: 'e60',
    drawerTimer: null,
    lastFocused: null
  };
  let productImages = {};
  let config = {
    currency: 'EUR',
    isLithuania: false,
    strings: {},
    properties: {}
  };

  function on(target, type, listener, options) {
    if (!target) return;
    target.addEventListener(type, listener, options);
    listenerCleanups.push(() => target.removeEventListener(type, listener, options));
  }

  function readCommerceConfig() {
    productImages = {};
    config = { currency: 'EUR', isLithuania: false, strings: {}, properties: {} };
    try {
      const imageData = document.querySelector('[data-cabinfix-product-images]');
      productImages = imageData ? JSON.parse(imageData.textContent) : {};
    } catch (error) {
      productImages = {};
    }

    try {
      const configData = document.querySelector('[data-cabinfix-config]');
      if (configData) config = Object.assign(config, JSON.parse(configData.textContent));
    } catch (error) {
      config = Object.assign(config, { strings: {}, properties: {} });
    }
  }

  function text(key, fallback) {
    return config.strings?.[key] || fallback || '';
  }

  function template(value, replacements) {
    return Object.keys(replacements || {}).reduce((output, key) => {
      return output.split(`__${key.toUpperCase()}__`).join(String(replacements[key]));
    }, String(value || ''));
  }

  function escapeHtml(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, function (character) {
      return {
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#039;'
      }[character];
    });
  }

  function formatMoney(cents) {
    const currency = state.cart?.currency || config.currency || 'EUR';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency
      }).format((Number(cents) || 0) / 100);
    } catch (error) {
      return `${((Number(cents) || 0) / 100).toFixed(2)} ${currency}`;
    }
  }

  function announce(message) {
    const status = document.querySelector('[data-cart-status]');
    if (status) status.textContent = message || '';
  }

  function showPurchaseError(form, message) {
    const error = form.querySelector('[data-purchase-error]');
    if (error) {
      error.textContent = message || '';
      error.hidden = !message;
    }
    if (message) announce(message);
    return error;
  }

  function showCartError(message) {
    const error = drawer?.querySelector('[data-cart-error]');
    const content = error?.querySelector('[data-cart-error-message]');
    if (content) content.textContent = message || '';
    if (error) error.hidden = !message;
    if (message) {
      drawer?.querySelectorAll('[data-cart-items], [data-cart-totals], [data-cart-empty]')
        .forEach((element) => { element.hidden = true; });
      const checkout = drawer?.querySelector('[data-cart-checkout]');
      checkout?.classList.add('is-disabled');
      checkout?.setAttribute('aria-disabled', 'true');
      announce(message);
    }
  }

  function invalidFitmentFields(fitment) {
    return Array.from(fitment.querySelectorAll('[data-fitment-field]')).filter((field) => {
      if (field.disabled || field.type === 'hidden') return false;
      const blankRequired = field.hasAttribute('required') && !String(field.value || '').trim();
      return blankRequired || (typeof field.checkValidity === 'function' && !field.checkValidity());
    });
  }

  function showFitmentValidation(fitment, invalidFields) {
    const error = fitment.querySelector('[data-fitment-error]');
    const description = fitment.querySelector('[data-fitment-error-message]');
    if (error) error.hidden = invalidFields.length === 0;
    fitment.querySelectorAll('[data-fitment-field]').forEach((field) => {
      const invalid = invalidFields.includes(field);
      if (invalid) field.setAttribute('aria-invalid', 'true');
      else field.removeAttribute('aria-invalid');
      if (!description?.id) return;
      const describedBy = (field.getAttribute('aria-describedby') || '').split(/\s+/)
        .filter((id) => id && id !== description.id);
      if (invalid) describedBy.push(description.id);
      if (describedBy.length) field.setAttribute('aria-describedby', describedBy.join(' '));
      else field.removeAttribute('aria-describedby');
    });
  }

  function validateFitment(form) {
    const fitment = form.querySelector('[data-order-fitment]');
    if (!fitment || !fitment.querySelector('[data-fitment-toggle]')?.checked) return true;
    const invalidFields = invalidFitmentFields(fitment);
    showFitmentValidation(fitment, invalidFields);
    if (!invalidFields.length) return true;
    announce(fitment.querySelector('[data-fitment-error-message]')?.textContent || '');
    invalidFields[0].scrollIntoView({ behavior: 'smooth', block: 'center' });
    invalidFields[0].focus({ preventScroll: true });
    return false;
  }

  function selectModel(model) {
    if (!['e60', 'e90'].includes(model)) return;
    state.activeModel = model;
    document.querySelectorAll('[data-model-button]').forEach((button) => {
      const selected = button.dataset.modelButton === model;
      button.classList.toggle('selected', selected);
      button.setAttribute('aria-pressed', selected ? 'true' : 'false');
    });
    document.querySelectorAll('[data-final-model-button]').forEach((button) => {
      const selected = button.dataset.finalModelButton === model;
      button.classList.toggle('selected', selected);
      button.setAttribute('aria-pressed', selected ? 'true' : 'false');
    });
    document.querySelectorAll('[data-product-panel]').forEach((panel) => {
      panel.classList.toggle('is-hidden', panel.dataset.productPanel !== model);
    });
    document.querySelectorAll('[data-model-content]').forEach((panel) => {
      panel.classList.toggle('is-hidden', panel.dataset.modelContent !== model);
    });
    document.querySelectorAll('[data-fitment-panel]').forEach((panel) => {
      panel.classList.toggle('is-hidden', panel.dataset.fitmentPanel !== model);
    });
    document.querySelectorAll('[data-final-product-panel]').forEach((panel) => {
      panel.classList.toggle('is-hidden', panel.dataset.finalProductPanel !== model);
    });

    const mobileModel = document.querySelector('[data-mobile-model]');
    if (mobileModel) mobileModel.textContent = model === 'e90' ? 'E90-E93' : 'E60 / E61';
    document.querySelectorAll('[data-mobile-start-price]').forEach((price) => {
      price.classList.toggle('is-hidden', price.dataset.mobileStartPrice !== model);
    });
    document.querySelectorAll('[data-pricing-model]').forEach((prices) => {
      prices.classList.toggle('is-hidden', prices.dataset.pricingModel !== model);
    });
    const mobileLink = document.querySelector('[data-mobile-buy-link]');
    if (mobileLink) mobileLink.href = model === 'e90' ? '#produktas-e90' : '#produktas-e60';
    syncMobileSelection();
    const unavailable = root?.querySelector('[data-selection-unavailable]');
    if (unavailable) unavailable.hidden = modelVariantForms(model).some((form) => form.querySelector('[data-variant-radio]'));
  }

  function setupGallery(gallery) {
    const main = gallery.querySelector('[data-gallery-main]');
    if (!main) return;
    const count = gallery.querySelector('[data-gallery-count]');
    const allThumbs = Array.from(gallery.querySelectorAll('[data-gallery-thumb]'));
    let activeIndex = 0;
    let touchStartX = 0;

    function visibleThumbs() {
      return allThumbs.filter((thumb) => !thumb.hidden);
    }

    function show(thumb) {
      const thumbs = visibleThumbs();
      if (!thumb || !thumbs.length) return;
      allThumbs.forEach((item) => item.classList.toggle('active', item === thumb));
      main.src = thumb.dataset.src;
      main.alt = thumb.dataset.alt || '';
      activeIndex = thumbs.indexOf(thumb);
      if (count) count.textContent = `${activeIndex + 1} / ${thumbs.length}`;
    }

    function move(direction) {
      const thumbs = visibleThumbs();
      if (!thumbs.length) return;
      activeIndex = (activeIndex + direction + thumbs.length) % thumbs.length;
      show(thumbs[activeIndex]);
    }

    allThumbs.forEach((thumb) => on(thumb, 'click', () => show(thumb)));
    on(gallery.querySelector('[data-gallery-prev]'), 'click', () => move(-1));
    on(gallery.querySelector('[data-gallery-next]'), 'click', () => move(1));

    on(main, 'touchstart', (event) => {
      touchStartX = event.changedTouches[0].screenX;
    }, { passive: true });
    on(main, 'touchend', (event) => {
      const distance = event.changedTouches[0].screenX - touchStartX;
      if (Math.abs(distance) > 45) move(distance > 0 ? -1 : 1);
    }, { passive: true });

    gallery.cabinFixFilter = function (kind) {
      allThumbs.forEach((thumb) => {
        thumb.hidden = Boolean(kind && kind !== 'pair' && thumb.dataset.side !== kind);
      });
      show(visibleThumbs()[0]);
    };
  }

  function fetchCart() {
    const cartUrl = routes.cartJson || '/cart.js';
    const separator = cartUrl.includes('?') ? '&' : '?';
    return fetch(`${cartUrl}${separator}_=${Date.now()}`, {
      method: 'GET',
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json' }
    }).then((response) => {
      if (!response.ok) throw new Error(text('cartUpdateError', 'Could not update the cart.'));
      return response.json();
    });
  }

  function cartLineTotal(item) {
    const quantity = Math.max(1, Number(item?.quantity) || 1);
    const unitPrice = Number(item?.final_price);
    const linePrice = Number(item?.final_line_price);
    const calculatedPrice = Number.isFinite(unitPrice) ? unitPrice * quantity : NaN;

    if (Number.isFinite(calculatedPrice) && calculatedPrice >= 0) return calculatedPrice;
    return Number.isFinite(linePrice) ? linePrice : 0;
  }

  function cartSubtotal(cart) {
    const items = Array.isArray(cart?.items) ? cart.items : [];
    const calculatedSubtotal = items.reduce((total, item) => total + cartLineTotal(item), 0);
    return items.length ? calculatedSubtotal : Number(cart?.total_price) || 0;
  }

  function detectVariantKind(variantTitle) {
    const variant = String(variantTitle || '').toLowerCase();
    const leftTerms = ['kair', 'left', 'lew', 'links', 'vasak'];
    const rightTerms = ['dešin', 'desin', 'right', 'praw', 'rechts', 'parem'];
    if (leftTerms.some((term) => variant.includes(term))) return 'left';
    if (rightTerms.some((term) => variant.includes(term))) return 'right';
    return 'pair';
  }

  function countLabel(type, quantity) {
    const oneKey = type === 'set' ? 'setOne' : type === 'part' ? 'partOne' : 'productOne';
    const manyKey = type === 'set' ? 'setMany' : type === 'part' ? 'partMany' : 'productMany';
    if (quantity === 1) return text(oneKey, type === 'set' ? '1 set' : type === 'part' ? '1 part' : '1 item');
    return template(text(manyKey, `__COUNT__ ${type}s`), { count: quantity });
  }

  function itemPresentation(item) {
    const source = `${item.handle || ''} ${item.product_title || ''}`.toLowerCase();
    const model = source.includes('e90') || source.includes('e91') || source.includes('e92') || source.includes('e93')
      ? 'e90'
      : source.includes('e60') || source.includes('e61')
        ? 'e60'
        : '';
    const kind = detectVariantKind(item.variant_title);
    const modelLabel = model === 'e90' ? 'BMW E90-E93' : model === 'e60' ? 'BMW E60/E61' : item.product_title || item.title;
    const shortModel = modelLabel.replace('BMW ', '');
    const quantity = Number(item.quantity) || 1;
    const isPair = kind === 'pair';
    const unitLabel = countLabel(isPair ? 'set' : 'part', quantity);
    const imageSet = productImages[model] || {};
    const fallbackImage = item.featured_image?.url || item.image || '';
    let images = [];

    if (!model) {
      return {
        model,
        kind: 'item',
        isPair: false,
        images: fallbackImage ? [fallbackImage] : [],
        unitLabel: countLabel('product', quantity),
        title: item.product_title || item.title,
        detail: item.variant_title && item.variant_title !== 'Default Title' ? item.variant_title : '',
        option: item.product_title || item.title
      };
    }

    if (isPair && imageSet.left && imageSet.right) images = [imageSet.left, imageSet.right];
    if (kind === 'left' && imageSet.left) images = [imageSet.left];
    if (kind === 'right' && imageSet.right) images = [imageSet.right];
    if (!images.length && fallbackImage) images = [fallbackImage];

    const sideLong = kind === 'left' ? text('leftGenitive', 'left') : text('rightGenitive', 'right');
    const sideShort = kind === 'left' ? text('leftShort', 'left') : text('rightShort', 'right');

    return {
      model,
      kind,
      isPair,
      images,
      unitLabel,
      title: isPair
        ? template(text('pairTitle', '__MODEL__ cup holder set'), { model: modelLabel })
        : template(text('sideTitle', '__MODEL__ __SIDE__ cup holder'), { model: modelLabel, side: sideLong }),
      detail: isPair
        ? text('pairDetail', 'Left + right - 2 parts')
        : kind === 'left' ? text('leftDetail', 'Left - driver side') : text('rightDetail', 'Right - passenger side'),
      option: isPair
        ? template(text('pairOption', '__MODEL__ - both sides'), { model: shortModel })
        : template(text('sideOption', '__MODEL__ - __SIDE__'), { model: shortModel, side: sideShort })
    };
  }

  function propertyValue(properties, labels) {
    for (const label of labels) {
      if (label && Object.prototype.hasOwnProperty.call(properties, label)) return properties[label];
    }
    return '';
  }

  function cartItemMarkup(item) {
    const presentation = itemPresentation(item);
    const quantity = Number(item.quantity) || 1;
    const images = presentation.images.map((image) => `<img src="${escapeHtml(image)}" alt="" width="144" height="144" loading="lazy">`).join('');
    const properties = item.properties || {};
    const checkValue = propertyValue(properties, [
      config.properties?.check,
      'Patikrinti suderinamumą',
      'Compatibility check',
      'Sprawdzenie kompatybilności',
      'Kompatibilitätsprüfung',
      'Sobivuse kontroll'
    ]);
    const yesValues = [config.properties?.yes, 'Taip', 'Yes', 'Tak', 'Ja', 'Jah'];
    const hasFitmentCheck = yesValues.includes(checkValue);
    const modelValue = propertyValue(properties, [
      config.properties?.model,
      'Automobilio modelis',
      'Car model',
      'Model samochodu',
      'Fahrzeugmodell',
      'Automudel'
    ]);
    const yearValue = propertyValue(properties, [
      config.properties?.year,
      'Gamybos metai',
      'Production year',
      'Rok produkcji',
      'Baujahr',
      'Tootmisaasta'
    ]);
    const fitmentDetails = [modelValue, yearValue].filter(Boolean).join(', ');

    return `
      <article class="cart-line${presentation.isPair ? ' cart-line-pair' : ''}" data-cart-line data-line-key="${escapeHtml(item.key)}" data-line-quantity="${quantity}">
        <div class="cart-line-image${presentation.isPair ? ' cart-line-image-pair' : ''}">
          ${images}
        </div>
        <div class="cart-line-copy">
          <strong>${escapeHtml(presentation.title)}</strong>
          <span>${escapeHtml(presentation.detail)}</span>
          <em>${escapeHtml(presentation.unitLabel)}</em>
          ${hasFitmentCheck ? `<div class="cart-fitment-confirmation"><strong><svg viewBox="0 0 24 24" aria-hidden="true"><path d="m5 12 4 4L19 6"/></svg>${escapeHtml(text('fitmentAdded', 'Fitment check added'))}</strong>${fitmentDetails ? `<span>${escapeHtml(fitmentDetails)}</span>` : ''}</div>` : ''}
          <div class="cart-line-controls">
            <div class="cart-quantity" aria-label="${escapeHtml(text('quantity', 'Quantity'))}">
              <button type="button" data-cart-quantity="-1" aria-label="${escapeHtml(text('decrease', 'Decrease quantity'))}">-</button>
              <span>${quantity}</span>
              <button type="button" data-cart-quantity="1" aria-label="${escapeHtml(text('increase', 'Increase quantity'))}">+</button>
            </div>
            <button type="button" class="cart-remove" data-cart-remove>${escapeHtml(text('remove', 'Remove'))}</button>
          </div>
        </div>
        <strong class="cart-line-price">${formatMoney(cartLineTotal(item))}</strong>
      </article>`;
  }

  function renderCart(cart, added) {
    state.cart = cart;
    const itemCount = Number(cart?.item_count) || 0;
    document.querySelectorAll('[data-cart-count]').forEach((node) => {
      node.textContent = itemCount;
      node.hidden = itemCount === 0;
    });
    document.documentElement.classList.toggle('cart-has-items', itemCount > 0);

    document.querySelectorAll('.desktop-cart-bubble').forEach((node) => {
      node.hidden = itemCount === 0;
    });
    const mobileDefault = document.querySelector('[data-mobile-buy-default]');
    const mobileSummary = document.querySelector('[data-mobile-cart-summary]');
    if (mobileDefault) mobileDefault.hidden = itemCount > 0;
    if (mobileSummary) mobileSummary.hidden = itemCount === 0;
    if (!drawer) return;

    const empty = drawer.querySelector('[data-cart-empty]');
    const items = drawer.querySelector('[data-cart-items]');
    const totals = drawer.querySelector('[data-cart-totals]');
    const title = drawer.querySelector('[data-cart-title]');
    const kicker = drawer.querySelector('[data-cart-kicker]');
    const checkout = drawer.querySelector('[data-cart-checkout]');

    showCartError('');
    if (items) items.hidden = false;

    if (empty) empty.hidden = itemCount > 0;
    if (totals) totals.hidden = itemCount === 0;
    if (items) items.innerHTML = itemCount > 0 ? cart.items.map(cartItemMarkup).join('') : '';
    syncCartFitment(cart);
    const primaryPresentation = itemCount > 0 ? itemPresentation(cart.items[0]) : null;
    if (title) {
      title.textContent = itemCount > 0
        ? cart.items.length === 1
          ? primaryPresentation.unitLabel
          : countLabel('product', itemCount)
        : text('choice', 'Your selection');
    }
    if (kicker) kicker.textContent = added ? text('addedKicker', 'Added to cart') : text('cartKicker', 'Cart');

    if (checkout) {
      checkout.classList.toggle('is-disabled', itemCount === 0);
      checkout.setAttribute('aria-disabled', itemCount === 0 ? 'true' : 'false');
    }

    if (itemCount > 0) {
      const subtotal = cartSubtotal(cart);
      const subtotalNode = drawer.querySelector('[data-cart-subtotal]');

      if (subtotalNode) subtotalNode.textContent = formatMoney(subtotal);

      const mobileOption = document.querySelector('[data-mobile-cart-option]');
      const mobileMeta = document.querySelector('[data-mobile-cart-meta]');
      if (mobileOption) {
        mobileOption.textContent = cart.items.length === 1
          ? primaryPresentation.option
          : template(text('choicesMany', '__COUNT__ selections'), { count: cart.items.length });
      }
      if (mobileMeta) {
        const count = cart.items.length === 1 ? primaryPresentation.unitLabel : countLabel('product', itemCount);
        const shippingText = text('shippingCheckout', 'delivery calculated at checkout');
        mobileMeta.textContent = `${count} - ${formatMoney(subtotal)} - ${shippingText}`;
      }
    }
  }

  function openCart(added) {
    if (!drawer || !overlay) return;
    window.clearTimeout(state.drawerTimer);
    if (drawer.hidden || !drawer.classList.contains('is-open')) state.lastFocused = document.activeElement;
    if (!drawer.hasAttribute('tabindex')) drawer.setAttribute('tabindex', '-1');
    drawer.hidden = false;
    overlay.hidden = false;
    requestAnimationFrame(() => {
      drawer.classList.add('is-open');
      overlay.classList.add('is-open');
      document.body.classList.add('cart-drawer-open');
      drawer.querySelector('[data-cart-close]')?.focus({ preventScroll: true });
    });

    if (state.cart) {
      renderCart(state.cart, added);
      return;
    }

    fetchCart().then((cart) => renderCart(cart, added)).catch((error) => {
      showCartError(added
        ? text('cartRefreshError', 'The item was added, but the cart could not refresh. Open the cart page to check it before trying again.')
        : error.message);
    });
  }

  function closeCart() {
    if (!drawer || !overlay || drawer.hidden) return;
    drawer.classList.remove('is-open');
    overlay.classList.remove('is-open');
    document.body.classList.remove('cart-drawer-open');
    state.drawerTimer = window.setTimeout(() => {
      drawer.hidden = true;
      overlay.hidden = true;
    }, 260);
    if (state.lastFocused && state.lastFocused.isConnected !== false && typeof state.lastFocused.focus === 'function') {
      state.lastFocused.focus({ preventScroll: true });
    }
  }

  function cartTabStops() {
    if (!drawer) return [];
    return Array.from(drawer.querySelectorAll('a[href], button, input, select, textarea, [tabindex]'))
      .filter((element) => !element.disabled
        && element.getAttribute('type') !== 'hidden'
        && element.getAttribute('aria-disabled') !== 'true'
        && element.tabIndex >= 0
        && !element.closest('[hidden], .is-hidden')
        && (typeof element.getClientRects !== 'function' || element.getClientRects().length > 0));
  }

  function addFormToCart(form) {
    if (state.cartBusy) return;
    showPurchaseError(form, '');
    const selected = form.querySelector('[data-variant-radio]:checked');
    const hiddenId = form.querySelector('[data-variant-id]');
    if (!selected || !selected.value || !hiddenId?.value || hiddenId.value !== selected.value) {
      promptSideSelection(form);
      return;
    }
    if (selected.disabled) {
      showPurchaseError(form, text('unavailable', 'This option is sold out.'));
      return;
    }
    if (!validateFitment(form)) return;
    if (typeof form.reportValidity === 'function' && !form.reportValidity()) return;
    const formData = new FormData(form);
    if (!formData.get('quantity')) formData.set('quantity', '1');

    state.cartBusy = true;
    refreshPurchaseControls();
    announce(text('addingAnnouncement', 'Adding the item to your cart.'));
    let itemAdded = false;
    const addUrl = routes.cartAdd || '/cart/add';

    fetch(addUrl.endsWith('.js') ? addUrl : `${addUrl}.js`, {
      method: 'POST',
      body: formData,
      credentials: 'same-origin',
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
    }).then((response) => {
      if (response.ok) return response.json();
      return response.json().catch(() => ({})).then((data) => {
        throw new Error(data.description || text('addError', 'Could not add the item. Please try again.'));
      });
    }).then((item) => {
      itemAdded = true;
      return fetchCart().then((cart) => ({ cart, item }));
    }).then(({ cart, item }) => {
      renderCart(cart, true);
      announce(template(text('addedAnnouncement', '__PRODUCT__ added to your cart.'), {
        product: item.product_title || text('itemFallback', 'Item')
      }));
      openCart(true);
    }).catch((error) => {
      // A failed cart refresh is not a failed add. Never retry the POST automatically.
      if (itemAdded) {
        state.cart = null;
        openCart(true);
        showCartError(text('cartRefreshError', 'The item was added, but the cart could not refresh. Open the cart page to check it before trying again.'));
      } else {
        const message = error.message || text('addError', 'Could not add the item. Please try again.');
        const errorNode = showPurchaseError(form, message);
        errorNode?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        errorNode?.focus({ preventScroll: true });
      }
    }).finally(() => {
      state.cartBusy = false;
      refreshPurchaseControls();
    });
  }

  function changeCartLine(key, quantity) {
    if (state.cartBusy) return;
    state.cartBusy = true;
    refreshPurchaseControls();
    drawer?.classList.add('is-updating');
    fetch(routes.cartChange || '/cart/change.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity: Math.max(0, quantity) })
    }).then((response) => {
      if (!response.ok) throw new Error(text('cartUpdateError', 'Could not update the cart.'));
      return response.json();
    }).then((cart) => {
      renderCart(cart, false);
      announce(text('cartUpdated', 'Cart updated.'));
    }).catch((error) => {
      announce(error.message);
      window.alert(error.message);
    }).finally(() => {
      state.cartBusy = false;
      refreshPurchaseControls();
      drawer?.classList.remove('is-updating');
    });
  }

  const fitmentPropertyAliases = {
    check: ['Patikrinti suderinamumą', 'Compatibility check', 'Sprawdzenie kompatybilności', 'Kompatibilitätsprüfung', 'Sobivuse kontroll'],
    model: ['Automobilio modelis', 'Car model', 'Model samochodu', 'Fahrzeugmodell', 'Automudel'],
    year: ['Gamybos metai', 'Production year', 'Rok produkcji', 'Baujahr', 'Tootmisaasta'],
    phone: ['Telefono numeris patikrai', 'Phone number for fitment check', 'Numer telefonu do weryfikacji', 'Telefonnummer für die Prüfung', 'Telefoninumber sobivuse kontrolliks'],
    vin: ['VIN paskutiniai 7 simboliai', 'Last 7 VIN characters', 'Ostatnie 7 znaków VIN', 'Letzte 7 Zeichen der FIN', 'VIN-koodi viimased 7 märki']
  };

  function cartFitmentLabels(form, key) {
    const attribute = 'property' + key.charAt(0).toUpperCase() + key.slice(1);
    return [form.dataset[attribute], config.properties?.[key], ...(fitmentPropertyAliases[key] || [])].filter(Boolean);
  }

  function eligibleFitmentItems(cart) {
    const knownVariants = new Map();
    document.querySelectorAll('[data-cart-form]').forEach((form) => {
      const model = variantFormModel(form);
      if (!['e60', 'e90'].includes(model)) return;
      form.querySelectorAll('[data-variant-radio]').forEach((radio) => knownVariants.set(String(radio.value), model));
    });
    return (Array.isArray(cart?.items) ? cart.items : []).filter((item) => {
      const expectedModel = knownVariants.get(String(item.variant_id || item.id));
      return item.key && expectedModel && itemPresentation(item).model === expectedModel;
    });
  }

  function syncCartFitmentFields(form, item) {
    const modelSelect = form.querySelector('[data-cart-fitment-model]');
    if (!modelSelect) return;
    const key = item?.key || '';
    if (form.dataset.loadedKey === key) return;
    form.dataset.loadedKey = key;
    const placeholder = modelSelect.dataset.placeholder || modelSelect.querySelector('option')?.textContent || '';
    modelSelect.dataset.placeholder = placeholder;
    const model = item ? itemPresentation(item).model : '';
    const codes = model === 'e90' ? ['E90', 'E91', 'E92', 'E93'] : model === 'e60' ? ['E60', 'E61'] : [];
    modelSelect.innerHTML = `<option value="">${escapeHtml(placeholder)}</option>`
      + codes.map((code) => `<option value="${code}">${code}</option>`).join('');
    const properties = item?.properties || {};
    ['model', 'year', 'phone', 'vin'].forEach((name) => {
      const field = form.querySelector(`[data-cart-fitment-${name}]`);
      if (!field) return;
      const value = String(propertyValue(properties, cartFitmentLabels(form, name)) || '');
      field.value = name === 'model' && !codes.includes(value.toUpperCase()) ? '' : name === 'model' ? value.toUpperCase() : value;
      field.removeAttribute('aria-invalid');
    });
    const error = form.querySelector('[data-cart-fitment-error]');
    if (error) error.hidden = true;
    const status = form.querySelector('[data-cart-fitment-status]');
    if (status) status.hidden = true;
  }

  function syncCartFitment(cart) {
    const editor = drawer?.querySelector('[data-cart-fitment]');
    const form = editor?.querySelector('[data-cart-fitment-form]');
    const target = form?.querySelector('[data-cart-fitment-target]');
    if (!editor || !form || !target) return;
    const items = eligibleFitmentItems(cart);
    editor.hidden = !items.length;
    if (!items.length) editor.open = false;
    const previousKey = target.value;
    const placeholder = target.dataset.placeholder || target.querySelector('option')?.textContent || '';
    target.dataset.placeholder = placeholder;
    target.innerHTML = `<option value="">${escapeHtml(placeholder)}</option>` + items.map((item) => {
      const presentation = itemPresentation(item);
      return `<option value="${escapeHtml(item.key)}">${escapeHtml(presentation.title)} (${Number(item.quantity) || 1})</option>`;
    }).join('');
    target.value = items.some((item) => item.key === previousKey) ? previousKey : items.length === 1 ? items[0].key : '';
    syncCartFitmentFields(form, items.find((item) => item.key === target.value));
  }

  function cartFitmentError(form, invalid, message) {
    const status = form.querySelector('[data-cart-fitment-status]');
    if (status) status.hidden = true;
    const error = form.querySelector('[data-cart-fitment-error]');
    if (error) {
      if (!error.dataset.validationMessage) error.dataset.validationMessage = error.textContent;
      error.textContent = message || error.dataset.validationMessage;
      error.hidden = false;
    }
    form.querySelectorAll('select, input').forEach((field) => {
      if (invalid.includes(field)) {
        field.setAttribute('aria-invalid', 'true');
        field.setAttribute('aria-describedby', 'CartFitmentError');
      } else {
        field.removeAttribute('aria-invalid');
        field.removeAttribute('aria-describedby');
      }
    });
    const focus = invalid[0] || form.querySelector('[data-cart-fitment-target]');
    focus?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    focus?.focus({ preventScroll: true });
    announce(error?.textContent || message);
  }

  function saveCartFitment(continueToCheckout) {
    if (state.cartBusy) return;
    const editor = drawer?.querySelector('[data-cart-fitment]');
    const form = editor?.querySelector('[data-cart-fitment-form]');
    if (!form) return;
    const fields = ['target', 'model', 'year', 'phone', 'vin'].reduce((all, name) => {
      all[name] = form.querySelector(`[data-cart-fitment-${name}]`);
      return all;
    }, {});
    const values = Object.keys(fields).reduce((all, name) => {
      all[name] = String(fields[name]?.value || '').trim();
      return all;
    }, {});
    const item = eligibleFitmentItems(state.cart).find((line) => line.key === values.target);
    const selectedModel = item ? itemPresentation(item).model : '';
    const expectedCodes = selectedModel === 'e90' ? ['E90', 'E91', 'E92', 'E93'] : selectedModel === 'e60' ? ['E60', 'E61'] : [];
    const invalid = ['target', 'model', 'year', 'phone'].filter((name) => !values[name] || (fields[name]?.checkValidity && !fields[name].checkValidity())).map((name) => fields[name]).filter(Boolean);
    if (!item && !invalid.includes(fields.target)) invalid.unshift(fields.target);
    if (!expectedCodes.includes(values.model) && !invalid.includes(fields.model)) invalid.push(fields.model);
    if (invalid.length) {
      cartFitmentError(form, invalid);
      return;
    }
    const error = form.querySelector('[data-cart-fitment-error]');
    if (error) error.hidden = true;
    const status = form.querySelector('[data-cart-fitment-status]');
    if (status) status.hidden = true;
    const checkoutUrl = drawer.querySelector('[data-cart-checkout]')?.href;
    state.cartBusy = true;
    refreshPurchaseControls();
    drawer.classList.add('is-updating');
    // A cart line key identifies this exact model/side/properties combination.
    // Never fall back to a variant ID: the same variant can occupy two lines.
    fetchCart().then((freshCart) => {
      const currentItem = eligibleFitmentItems(freshCart).find((line) => line.key === values.target);
      if (!currentItem) {
        renderCart(freshCart, false);
        throw new Error(form.dataset.saveError);
      }
      const properties = Object.assign({}, currentItem.properties || {});
      ['check', 'model', 'year', 'phone', 'vin'].forEach((name) => {
        const labels = cartFitmentLabels(form, name);
        const label = labels.find((candidate) => Object.prototype.hasOwnProperty.call(properties, candidate)) || labels[0];
        if (!label) return;
        const value = name === 'check' ? form.dataset.propertyYes : values[name];
        if (name !== 'vin' || value || Object.prototype.hasOwnProperty.call(properties, label)) properties[label] = value;
      });
      return fetch(routes.cartChange || '/cart/change.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: currentItem.key, quantity: currentItem.quantity, properties })
      }).then((response) => {
        if (!response.ok) throw new Error(form.dataset.saveError);
        return response.json();
      });
    }).then((cart) => {
      if (!Array.isArray(cart?.items)) throw new Error(form.dataset.saveError);
      renderCart(cart, false);
      const currentEditor = drawer?.querySelector('[data-cart-fitment]');
      if (currentEditor) currentEditor.open = false;
      if (!continueToCheckout) currentEditor?.querySelector('summary')?.focus({ preventScroll: true });
      const currentStatus = drawer?.querySelector('[data-cart-fitment-status]');
      if (currentStatus) {
        currentStatus.textContent = form.dataset.savedMessage;
        currentStatus.hidden = false;
      }
      announce(form.dataset.savedMessage);
      if (continueToCheckout && checkoutUrl) window.location.assign(checkoutUrl);
    }).catch((errorObject) => {
      // A timed-out POST might already have succeeded. Refresh, but never
      // repeat the mutation or add a replacement cart item automatically.
      return fetchCart().then((cart) => renderCart(cart, false)).catch(() => {}).then(() => {
        const currentEditor = drawer?.querySelector('[data-cart-fitment]');
        if (currentEditor) currentEditor.open = true;
        const currentForm = currentEditor?.querySelector('[data-cart-fitment-form]');
        if (currentForm) cartFitmentError(currentForm, [], errorObject.message || form.dataset.saveError);
      });
    }).finally(() => {
      state.cartBusy = false;
      drawer?.classList.remove('is-updating');
      refreshPurchaseControls();
    });
  }

  function setupCartFitment() {
    const editor = drawer?.querySelector('[data-cart-fitment]');
    const form = editor?.querySelector('[data-cart-fitment-form]');
    if (!form) return;
    on(form.querySelector('[data-cart-fitment-target]'), 'change', (event) => {
      const item = eligibleFitmentItems(state.cart).find((line) => line.key === event.target.value);
      syncCartFitmentFields(form, item);
    });
    on(form, 'submit', (event) => {
      event.preventDefault();
      saveCartFitment(false);
    });
    on(form.querySelector('[data-cart-fitment-skip]'), 'click', () => {
      if (state.cartBusy) return;
      editor.open = false;
      // Existing saved checks remain untouched. Skip only this new request.
      drawer.querySelector('[data-cart-checkout]')?.click();
    });
    if (state.cart) syncCartFitment(state.cart);
  }

  function variantFormModel(form) {
    const panel = form.closest('[data-product-panel], [data-final-product-panel]');
    return panel?.dataset.productPanel || panel?.dataset.finalProductPanel || '';
  }

  function modelVariantForms(model) {
    return Array.from(document.querySelectorAll('[data-cart-form]'))
      .filter((form) => variantFormModel(form) === model);
  }

  function refreshFormPurchaseControl(form) {
    const selected = form.querySelector('[data-variant-radio]:checked');
    const button = form.querySelector('[data-add-to-cart]');
    const label = button?.querySelector('[data-add-label]') || button?.querySelector('span');
    if (button) {
      // Neutral remains actionable: it explains the missing choice and focuses it.
      button.disabled = state.cartBusy || Boolean(selected?.disabled);
      button.setAttribute('aria-busy', state.cartBusy ? 'true' : 'false');
    }
    if (label) label.textContent = state.cartBusy
      ? text('adding', 'Adding...')
      : selected?.disabled
        ? text('unavailable', 'Sold out')
        : selected
          ? text('addLabel', 'Add to cart')
          : text('chooseSide', 'Choose a side');
  }

  function refreshPurchaseControls() {
    document.querySelectorAll('[data-cart-form]').forEach(refreshFormPurchaseControl);
    document.querySelectorAll('[data-fitment-skip]').forEach((button) => { button.disabled = state.cartBusy; });
    drawer?.querySelectorAll('[data-cart-quantity], [data-cart-remove], [data-cart-fitment-save], [data-cart-fitment-skip]')
      .forEach((button) => { button.disabled = state.cartBusy; });
    const checkout = drawer?.querySelector('[data-cart-checkout]');
    const disabled = state.cartBusy || !state.cart?.item_count;
    checkout?.setAttribute('aria-disabled', disabled ? 'true' : 'false');
    checkout?.classList.toggle('is-disabled', disabled);
    syncMobileSelection();
  }

  function syncMobileSelection() {
    const form = modelVariantForms(state.activeModel)[0];
    const selected = form?.querySelector('[data-variant-radio]:checked');
    const button = document.querySelector('[data-mobile-add-to-cart]');
    const label = document.querySelector('[data-mobile-action-label]');
    const price = document.querySelector('[data-mobile-selected-price]');
    const side = document.querySelector('[data-mobile-selected-side]');
    if (price) price.textContent = selected?.dataset.price || form?.dataset.neutralPrice || '';
    if (side) side.textContent = selected?.dataset.sideLabel || (selected
      ? selected.dataset.kind === 'pair'
        ? text('bothSides', 'Both sides')
        : selected.dataset.kind === 'left'
          ? text('leftShort', 'Left')
          : text('rightShort', 'Right')
      : text('chooseSide', 'Choose a side'));
    if (button) {
      button.disabled = !form || state.cartBusy || Boolean(selected?.disabled);
      button.setAttribute('aria-busy', state.cartBusy ? 'true' : 'false');
    }
    if (label) label.textContent = state.cartBusy
      ? text('adding', 'Adding...')
      : selected?.disabled
        ? text('unavailable', 'Sold out')
        : selected
          ? text('addLabel', 'Add to cart')
          : text('chooseSide', 'Choose a side');
  }

  function promptSideSelection(form) {
    showPurchaseError(form, text('chooseSideHint', 'Choose the left side, right side or both sides first.'));
    const options = form.querySelector('[data-side-options], .variant-grid') || form;
    options.scrollIntoView({ behavior: 'smooth', block: 'center' });
    // Focus a single available choice without selecting it. The group itself
    // is not focusable, so clicking a label cannot highlight the whole group.
    const firstAvailable = Array.from(form.querySelectorAll('[data-variant-radio]'))
      .find((radio) => !radio.disabled);
    if (firstAvailable) firstAvailable.focus({ preventScroll: true });
  }

  function clearFormVariant(form) {
    form.querySelectorAll('[data-variant-radio]').forEach((radio) => { radio.checked = false; });
    form.querySelectorAll('.variant-card.selected, .quick-variant-card.selected')
      .forEach((card) => card.classList.remove('selected'));
    const hiddenId = form.querySelector('[data-variant-id]');
    const price = form.querySelector('[data-current-price]');
    const title = form.querySelector('[data-selected-title]');
    const shipping = form.querySelector('[data-selected-shipping], [data-shipping-note]');
    if (hiddenId) hiddenId.value = '';
    if (price) price.textContent = '';
    if (title) title.textContent = text('chooseSide', 'Choose a side');
    if (shipping) shipping.textContent = shipping.dataset.neutralShipping || text('shippingCheckout', 'Delivery calculated at checkout');
    const gallery = form.closest('[data-product-panel]')?.querySelector('[data-product-gallery]');
    if (gallery?.cabinFixFilter) gallery.cabinFixFilter('');
    refreshFormPurchaseControl(form);
  }

  function applyFormVariant(form, radio) {
    showPurchaseError(form, '');
    if (!radio) {
      clearFormVariant(form);
      return;
    }
    const panel = form.closest('[data-product-panel]');
    const gallery = panel?.querySelector('[data-product-gallery]');
    const hiddenId = form.querySelector('[data-variant-id]');
    const price = form.querySelector('[data-current-price]');
    const title = form.querySelector('[data-selected-title]');
    const shipping = form.querySelector('[data-selected-shipping], [data-shipping-note]');
    form.querySelectorAll('[data-variant-radio]').forEach((option) => {
      option.checked = option === radio;
    });
    if (hiddenId) hiddenId.value = radio.value;
    if (price) price.textContent = radio.dataset.price;
    if (title) title.textContent = radio.dataset.title;
    if (shipping) shipping.textContent = radio.dataset.shipping || text('shippingCheckout', 'Delivery calculated at checkout');
    refreshFormPurchaseControl(form);
    form.querySelectorAll('.variant-card.selected, .quick-variant-card.selected').forEach((card) => card.classList.remove('selected'));
    const label = form.querySelector(`label[for="${CSS.escape(radio.id)}"]`);
    label?.classList.add('selected');
    if (gallery?.cabinFixFilter) gallery.cabinFixFilter(radio.dataset.kind);
  }

  // All controls for one model submit the same variant, not just the same label.
  function selectModelVariant(model, variantId) {
    modelVariantForms(model).forEach((form) => {
      const radio = Array.from(form.querySelectorAll('[data-variant-radio]'))
        .find((option) => option.value === variantId);
      applyFormVariant(form, radio);
    });
    syncMobileSelection();
  }

  function setupVariantForm(form) {
    const model = variantFormModel(form);

    form.querySelectorAll('[data-variant-radio]').forEach((radio) => {
      on(radio, 'change', () => {
        if (!radio.checked || radio.disabled) return;
        if (model) {
          selectModelVariant(model, radio.value);
          syncModelUrl(model);
        } else {
          applyFormVariant(form, radio);
        }
      });
    });
    clearFormVariant(form);

    on(form, 'submit', (event) => {
      event.preventDefault();
      addFormToCart(form);
    });
    // Keep native validation without JS. With JS, both purchase buttons use
    // the same visible validation instead of silently stopping before submit.
    form.noValidate = true;
  }

  function setupFitmentCheck(fitment) {
    const toggle = fitment.querySelector('[data-fitment-toggle]');
    const fields = fitment.querySelector('[data-fitment-fields]');
    if (!toggle || !fields) return;

    const sync = function () {
      fields.hidden = !toggle.checked;
      toggle.setAttribute('aria-expanded', toggle.checked ? 'true' : 'false');
      fields.querySelectorAll('[data-fitment-field]').forEach((field) => {
        field.disabled = !toggle.checked;
      });
      fitment.classList.toggle('is-open', toggle.checked);
      showFitmentValidation(fitment, []);
    };

    on(toggle, 'change', sync);
    fields.querySelectorAll('[data-fitment-field]').forEach((field) => {
      const refreshValidation = function () {
        if (fitment.querySelector('[data-fitment-error]')?.hidden === false) {
          showFitmentValidation(fitment, invalidFitmentFields(fitment));
        }
      };
      on(field, 'input', refreshValidation);
      on(field, 'change', refreshValidation);
    });
    on(fitment.querySelector('[data-fitment-skip]'), 'click', () => {
      if (state.cartBusy) return;
      const form = fitment.closest('[data-cart-form]');
      if (!form) return;
      toggle.checked = false;
      // Disable every fitment property (including the hidden request flag),
      // without deleting the customer's entries if they enable it again.
      sync();
      form.querySelector('[data-add-to-cart]')?.focus({ preventScroll: true });
      addFormToCart(form);
    });
    sync();
  }

  function setupMobileBuyBarVisibility() {
    const bar = document.querySelector('[data-mobile-buy-bar]');
    const buyButtons = Array.from(document.querySelectorAll('[data-add-to-cart]'));
    if (!bar || !buyButtons.length || !('IntersectionObserver' in window)) return;

    const visibleButtons = new Set();
    const sync = function () {
      const suppressed = visibleButtons.size > 0;
      bar.classList.toggle('is-suppressed', suppressed);
      bar.setAttribute('aria-hidden', suppressed ? 'true' : 'false');
      bar.inert = suppressed;
    };
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && entry.intersectionRatio > 0) visibleButtons.add(entry.target);
        else visibleButtons.delete(entry.target);
      });
      sync();
    }, { threshold: [0, 0.01] });

    buyButtons.forEach((button) => observer.observe(button));
    observers.push(observer);
  }

  function requestedModel() {
    const params = new URLSearchParams(window.location.search);
    const queryModel = String(params.get('model') || '').toLowerCase();
    const hashModel = String(window.location.hash || '').toLowerCase();
    if (queryModel === 'e60' || queryModel === 'e90') return queryModel;
    if (hashModel.includes('e90')) return 'e90';
    return root?.dataset.defaultModel === 'e90' ? 'e90' : 'e60';
  }

  function restoreRequestedSelection() {
    const model = requestedModel();
    const params = new URLSearchParams(window.location.search);
    const side = String(params.get('side') || '').toLowerCase();
    const form = modelVariantForms(model)[0];
    const radios = Array.from(form?.querySelectorAll('[data-variant-radio]') || []);
    const requested = ['left', 'right', 'pair'].includes(side)
      ? radios.find((radio) => radio.dataset.kind === side)
      : null;
    // A plain/model-only link has no implied side. Explicit sold-out choices stay selected.
    document.querySelectorAll('[data-cart-form]').forEach(clearFormVariant);
    selectModel(model);
    if (requested) selectModelVariant(model, requested.value);
    syncModelUrl(model);
  }

  function switchModel(model) {
    if (!['e60', 'e90'].includes(model)) return;
    const current = modelVariantForms(state.activeModel)[0]?.querySelector('[data-variant-radio]:checked');
    const kind = current?.dataset.kind;
    const nextForm = modelVariantForms(model)[0];
    const matching = Array.from(nextForm?.querySelectorAll('[data-variant-radio]') || [])
      .find((radio) => kind && radio.dataset.kind === kind);
    // Keep the user's current side, never an old/default pair from the other model.
    selectModel(model);
    if (matching) selectModelVariant(model, matching.value);
    else modelVariantForms(model).forEach(clearFormVariant);
    syncMobileSelection();
    syncModelUrl(model);
  }

  function selectionUrl(model) {
    const url = new URL(window.location.href);
    url.searchParams.set('model', model);
    const selected = modelVariantForms(model)[0]?.querySelector('[data-variant-radio]:checked');
    if (selected) {
      url.searchParams.set('side', selected.dataset.kind);
      if (url.searchParams.has('variant')) url.searchParams.set('variant', selected.value);
    } else {
      url.searchParams.delete('side');
      url.searchParams.delete('variant');
    }
    if (/^#produktas-e(?:60|90)$/i.test(url.hash)) url.hash = `produktas-${model}`;
    return url;
  }

  function syncSelectionLinks(model) {
    if (!root) return;
    document.querySelectorAll('[data-selection-home-link], [data-product-link]').forEach((link) => {
      if (!Object.prototype.hasOwnProperty.call(link.dataset, 'selectionTargetHash')) {
        link.dataset.selectionTargetHash = new URL(link.href, window.location.href).hash;
      }
      const url = selectionUrl(model);
      url.hash = link.hasAttribute('data-product-link') ? `produktas-${model}` : link.dataset.selectionTargetHash;
      link.href = `${url.pathname}${url.search}${url.hash}`;
    });
    document.querySelectorAll('[data-compat-model]').forEach((link) => {
      const url = new URL(window.location.href);
      url.searchParams.set('model', link.dataset.compatModel);
      url.searchParams.delete('side');
      url.searchParams.delete('variant');
      url.hash = 'dv-buy';
      link.href = `${url.pathname}${url.search}${url.hash}`;
    });
  }

  function syncModelUrl(model) {
    if (window.history?.replaceState) {
      const url = selectionUrl(model);
      window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
    }
    syncSelectionLinks(model);
    document.dispatchEvent(new Event('detalora:selectionchange'));
  }

  // Detalora's language-only menu also selects its matching Shopify Market.
  // Prices remain Shopify presentment prices: never convert amounts in the browser.
  function setupLanguageMarketSelect(select) {
    const form = select.form;
    const countryInput = form?.querySelector('[name="country_code"]');
    if (!form || !countryInput) return;
    select.dataset.localizationReady = 'true';

    const currentCountry = select.dataset.currentCountry || countryInput.value;

    function selection() {
      const option = select.options[select.selectedIndex];
      return { language: select.value, country: option?.dataset.countryCode || '' };
    }

    function attemptKey(target) {
      const url = new URL(window.location.href);
      const theme = url.searchParams.get('preview_theme_id') || select.dataset.themeId || 'live';
      return `detalora:localization:v9:${theme}:${target.language}:${target.country}`;
    }

    function submitLocalization(target) {
      countryInput.value = target.country || currentCountry;
      const url = new URL(window.location.href);
      // Keep the current page, product selection, attribution and preview session.
      // Old forced-localization query parameters must not override this POST.
      ['country', 'country_code', 'currency', 'locale', 'language', 'language_code'].forEach((key) => {
        url.searchParams.delete(key);
      });
      let returnTo = form.querySelector('[name="return_to"]');
      if (!returnTo) {
        returnTo = document.createElement('input');
        returnTo.type = 'hidden';
        returnTo.name = 'return_to';
        form.appendChild(returnTo);
      }
      returnTo.value = `${url.pathname}${url.search}${url.hash}`;
      form.submit();
    }

    on(select, 'change', () => {
      const target = selection();
      // Explicit user changes work even when browser storage is unavailable.
      try { window.sessionStorage.setItem(attemptKey(target), 'attempted'); } catch (_) { /* Optional guard. */ }
      submitLocalization(target);
    });

    const target = selection();
    // Align direct /pl visits and old market cookies as well as menu changes.
    // Never auto-navigate inside the theme editor, or guess a market for EN/DE.
    if (!target.country || target.country === currentCountry || select.dataset.designMode === 'true') return;
    try {
      const key = attemptKey(target);
      if (window.sessionStorage.getItem(key)) return;
      window.sessionStorage.setItem(key, 'attempted');
    } catch (_) {
      // Without persistent loop protection, automatic navigation is unsafe.
      return;
    }
    submitLocalization(target);
  }

  function initializeCommerce() {
    listenerCleanups.splice(0).forEach((cleanup) => cleanup());
    observers.splice(0).forEach((observer) => observer.disconnect());
    const previousDrawer = drawer;
    root = document.querySelector('[data-cabinfix-root]');
    drawer = document.querySelector('[data-cart-drawer]');
    overlay = document.querySelector('[data-cart-overlay]');
    if (previousDrawer && previousDrawer !== drawer) {
      window.clearTimeout(state.drawerTimer);
      document.body.classList.remove('cart-drawer-open');
    }
    readCommerceConfig();
    document.querySelectorAll('[data-language-select]').forEach(setupLanguageMarketSelect);

  document.querySelectorAll('[data-model-button]').forEach((button) => {
    on(button, 'click', () => {
      switchModel(button.dataset.modelButton);
    });
  });

  document.querySelectorAll('[data-compat-model]').forEach((link) => {
    on(link, 'click', (event) => {
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button > 0) return;
      event.preventDefault();
      switchModel(link.dataset.compatModel);
      const section = document.querySelector('#dv-buy');
      const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      section?.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'start' });
      document.querySelector('[data-model-button="' + link.dataset.compatModel + '"]')?.focus({ preventScroll: true });
    });
  });
  document.querySelectorAll('[data-final-model-button]').forEach((button) => {
    on(button, 'click', () => {
      switchModel(button.dataset.finalModelButton);
    });
  });

  document.querySelectorAll('[data-product-gallery]').forEach(setupGallery);
  document.querySelectorAll('[data-cart-form]').forEach(setupVariantForm);
  document.querySelectorAll('[data-order-fitment]').forEach(setupFitmentCheck);
  setupMobileBuyBarVisibility();

  on(document.querySelector('[data-mobile-add-to-cart]'), 'click', () => {
    if (state.cartBusy) return;
    const form = modelVariantForms(state.activeModel)[0];
    if (!form) return;
    addFormToCart(form);
  });

  document.querySelectorAll('[data-fitment-jump]').forEach((trigger) => {
    on(trigger, 'click', () => {
      const panel = Array.from(document.querySelectorAll('[data-product-panel]')).find((item) => !item.classList.contains('is-hidden'));
      const fitment = panel?.querySelector('[data-order-fitment]');
      const toggle = fitment?.querySelector('[data-fitment-toggle]');
      if (!fitment || !toggle) return;
      toggle.checked = true;
      toggle.dispatchEvent(new Event('change', { bubbles: true }));
      fitment.scrollIntoView({ behavior: 'smooth', block: 'center' });
      window.setTimeout(() => fitment.querySelector('select')?.focus({ preventScroll: true }), 450);
    });
  });

  document.querySelectorAll('[data-cart-open]').forEach((trigger) => {
    on(trigger, 'click', (event) => {
      if (!drawer) return;
      event.preventDefault();
      openCart(false);
    });
  });
  document.querySelectorAll('[data-cart-close]').forEach((trigger) => on(trigger, 'click', closeCart));
  on(overlay, 'click', closeCart);

  on(drawer, 'click', (event) => {
    if (state.cartBusy) return;
    const line = event.target.closest('[data-cart-line]');
    if (!line) return;
    const key = line.dataset.lineKey;
    const quantity = Number(line.dataset.lineQuantity) || 1;
    const quantityButton = event.target.closest('[data-cart-quantity]');
    const removeButton = event.target.closest('[data-cart-remove]');
    if (quantityButton) changeCartLine(key, quantity + Number(quantityButton.dataset.cartQuantity));
    if (removeButton) changeCartLine(key, 0);
  });

  on(drawer?.querySelector('[data-cart-checkout]'), 'click', (event) => {
    if (!state.cart?.item_count || state.cartBusy || event.currentTarget.getAttribute('aria-disabled') === 'true') {
      event.preventDefault();
      return;
    }
    const editor = drawer.querySelector('[data-cart-fitment]');
    if (!editor?.open) return;
    event.preventDefault();
    saveCartFitment(true);
  });

  on(document, 'keydown', (event) => {
    if (!drawer || drawer.hidden || !drawer.classList.contains('is-open')) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      closeCart();
      return;
    }
    if (event.key !== 'Tab') return;
    const stops = cartTabStops();
    const first = stops[0];
    const last = stops[stops.length - 1];
    const active = document.activeElement;
    if (!first) {
      event.preventDefault();
      drawer.focus({ preventScroll: true });
    } else if (!drawer.contains(active) || active === drawer) {
      event.preventDefault();
      (event.shiftKey ? last : first).focus({ preventScroll: true });
    } else if (event.shiftKey && active === first) {
      event.preventDefault();
      last.focus({ preventScroll: true });
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus({ preventScroll: true });
    }
  });

  if (root) {
    restoreRequestedSelection();
    on(window, 'popstate', restoreRequestedSelection);
    on(window, 'hashchange', () => {
      const match = window.location.hash.match(/^#produktas-(e60|e90)$/i);
      if (match) switchModel(match[1].toLowerCase());
    });
    fetchCart().then((cart) => renderCart(cart, false)).catch(() => {});
    on(window, 'pageshow', () => {
      restoreRequestedSelection();
      if (state.cartBusy) return;
      fetchCart().then((cart) => renderCart(cart, false)).catch(() => {});
    });
    on(document, 'visibilitychange', () => {
      if (!document.hidden && !state.cartBusy) fetchCart().then((cart) => renderCart(cart, false)).catch(() => {});
    });
    root.classList.add('is-selection-ready');
    const unavailable = root.querySelector('[data-selection-unavailable]');
    if (unavailable) unavailable.hidden = modelVariantForms(state.activeModel).some((form) => form.querySelector('[data-variant-radio]'));
  }
    setupCartFitment();
    refreshPurchaseControls();
  }

  function scheduleReinitialize() {
    window.clearTimeout(reinitializeTimer);
    reinitializeTimer = window.setTimeout(initializeCommerce, 0);
  }

  window.detaloraCommerce = { reinitialize: initializeCommerce };
  document.addEventListener('shopify:section:load', scheduleReinitialize);
  document.addEventListener('shopify:section:unload', scheduleReinitialize);
  initializeCommerce();
})();
